import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import { getTeamLabel } from '../utils/teamLabels'
import { buildTeamReview, buildInsightFacts, eligibleFixtures, teamNumbersIn } from '../utils/seasonReview'

const BLUE = '#185FA5'
const GREEN = '#3B6D11'
const RED = '#A32D2D'
const AMBER = '#854F0B'
const GREY = '#888'

const printCss = `
@media print {
  body * { visibility: hidden; }
  #season-review-print, #season-review-print * { visibility: visible; }
  #season-review-print { position: absolute; left: 0; top: 0; width: 100%; }
  .no-print { display: none !important; }
  .page-break { page-break-before: always; }
}
#season-review-print { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`

const th = { textAlign: 'left', fontSize: 10, color: GREY, fontWeight: 600, padding: '6px 6px', textTransform: 'uppercase', borderBottom: '1px solid #e6e9ef', whiteSpace: 'nowrap' }
const td = { fontSize: 12, padding: '6px 6px', borderBottom: '0.5px solid #f0f0f0', whiteSpace: 'nowrap' }

const pad = n => String(n || 0).padStart(2, '0')
const per10 = (v, m) => (m > 0 ? Math.round((v / m) * 100) / 10 : 0)
const shortDate = d => new Date(d).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })
const resultColor = r => (r === 'W' ? GREEN : r === 'L' ? RED : AMBER)
const resultBg = r => (r === 'W' ? '#EAF3DE' : r === 'L' ? '#FCEBEB' : '#FAEEDA')

async function fetchAllEvents(ids) {
  const rows = []
  const size = 1000
  let from = 0
  while (true) {
    const { data, error } = await supabase.from('match_events')
      .select('*, players(id, full_name, jersey_number)')
      .in('fixture_id', ids)
      .order('id', { ascending: true })
      .range(from, from + size - 1)
    if (error) throw error
    rows.push(...(data || []))
    if (!data || data.length < size) break
    from += size
  }
  return rows
}

function Tile({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 70px', background: '#f7f9fc', borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#222', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, color: GREY, marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    </div>
  )
}

function SectionTitle({ children, note }) {
  return (
    <div style={{ margin: '22px 0 8px' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: BLUE }}>{children}</div>
      {note && <div style={{ fontSize: 11, color: GREY, marginTop: 2 }}>{note}</div>}
    </div>
  )
}

function MatchFlow({ game, matchLen, halfLen }) {
  const W = 320
  const H = 160
  const padL = 28
  const padR = 8
  const padT = 10
  const padB = 18
  const maxAbs = Math.max(8, ...game.flow.map(p => Math.abs(p.margin))) + 1
  const plotH = H - padT - padB
  const midY = padT + plotH / 2
  const x = m => padL + (Math.min(m, matchLen) / matchLen) * (W - padL - padR)
  const y = v => midY - (v / maxAbs) * (plotH / 2)
  const pts = game.flow
  let d = 'M ' + x(pts[0].minute) + ' ' + y(pts[0].margin)
  for (let i = 1; i < pts.length; i++) {
    d += ' L ' + x(pts[i].minute) + ' ' + y(pts[i - 1].margin) + ' L ' + x(pts[i].minute) + ' ' + y(pts[i].margin)
  }
  d += ' L ' + x(matchLen) + ' ' + y(pts[pts.length - 1].margin)
  return (
    <div style={{ border: '1px solid #eceff4', borderRadius: 10, padding: '10px 12px', breakInside: 'avoid' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{game.opponent}</span>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: resultBg(game.result), color: resultColor(game.result) }}>
          {game.us}–{game.them}
        </span>
      </div>
      <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', display: 'block' }}>
        <rect x={padL} y={padT} width={W - padL - padR} height={plotH / 2} fill="#F3F8EC" />
        <rect x={padL} y={midY} width={W - padL - padR} height={plotH / 2} fill="#FBF0F0" />
        <line x1={padL} x2={W - padR} y1={midY} y2={midY} stroke="#b8c1cf" strokeWidth="0.8" />
        <line x1={x(halfLen)} x2={x(halfLen)} y1={padT} y2={H - padB} stroke="#b8c1cf" strokeWidth="0.8" strokeDasharray="3 3" />
        <text x={padL - 4} y={padT + 6} fontSize="8" textAnchor="end" fill={GREY}>+{maxAbs}</text>
        <text x={padL - 4} y={midY + 3} fontSize="8" textAnchor="end" fill={GREY}>0</text>
        <text x={padL - 4} y={H - padB} fontSize="8" textAnchor="end" fill={GREY}>-{maxAbs}</text>
        <path d={d} fill="none" stroke={BLUE} strokeWidth="1.8" />
        {pts.slice(1).map((p, i) => (
          <circle key={i} cx={x(p.minute)} cy={y(p.margin)} r={p.value === 3 ? 3.8 : 2.4} fill={p.side === 'us' ? GREEN : RED} />
        ))}
        <text x={x(0)} y={H - 4} fontSize="8" textAnchor="start" fill={GREY}>0'</text>
        <text x={x(halfLen)} y={H - 4} fontSize="8" textAnchor="middle" fill={GREY}>HT</text>
        <text x={x(matchLen)} y={H - 4} fontSize="8" textAnchor="end" fill={GREY}>{matchLen}'</text>
      </svg>
    </div>
  )
}

function BandsChart({ bands }) {
  const W = 300
  const H = 120
  const base = 100
  const top = 16
  const maxV = Math.max(1, ...bands.flatMap(b => [b.us, b.them]))
  const slot = (W - 10) / bands.length
  const h = v => (v / maxV) * (base - top)
  return (
    <div style={{ maxWidth: 560 }}>
      <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', display: 'block' }}>
        <line x1="5" x2={W - 5} y1={base} y2={base} stroke="#cfd6e0" strokeWidth="0.6" />
        {bands.map((b, i) => {
          const x0 = 5 + i * slot + slot * 0.08
          const bw = slot * 0.36
          return (
            <g key={i}>
              <rect x={x0} y={base - h(b.us)} width={bw} height={h(b.us)} fill={BLUE} rx="1.5" />
              <rect x={x0 + bw + 1} y={base - h(b.them)} width={bw} height={h(b.them)} fill="#E08A3C" rx="1.5" />
              <text x={x0 + bw / 2} y={base - h(b.us) - 3} fontSize="7" textAnchor="middle" fill="#444">{b.us}</text>
              <text x={x0 + bw + 1 + bw / 2} y={base - h(b.them) - 3} fontSize="7" textAnchor="middle" fill="#444">{b.them}</text>
              <text x={5 + i * slot + slot / 2} y={base + 11} fontSize="7" textAnchor="middle" fill={GREY}>{Math.round(b.from)}–{Math.round(b.to)}</text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function TakeawayList({ title, icon, items }) {
  if (!items || items.length === 0) return null
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 4 }}>{icon} {title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.65, color: '#333' }}>
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  )
}

function TeamSection({ review, label, fixtures, halfLengthMins, first, narrative, sportLabel }) {
  const s = review.summary
  const showTwoPoints = sportLabel === 'football'
  const matchLen = halfLengthMins * 2
  const fxById = Object.fromEntries(fixtures.map(f => [f.id, f]))
  const fullGames = review.games.filter(g => g.flow)
  const subGames = review.games.filter(g => g.subs && g.subs.subsMade > 0)
  const st = subGames.reduce((t, g) => ({
    bUs: t.bUs + g.subs.beforeFirst.us, bThem: t.bThem + g.subs.beforeFirst.them, bMin: t.bMin + g.subs.beforeFirst.minutes,
    aUs: t.aUs + g.subs.afterFirst.us, aThem: t.aThem + g.subs.afterFirst.them, aMin: t.aMin + g.subs.afterFirst.minutes,
  }), { bUs: 0, bThem: 0, bMin: 0, aUs: 0, aThem: 0, aMin: 0 })
  const maxTotal = Math.max(1, ...review.topScorers.map(p => p.total))
  const spot = review.spotlights
  const hasSpot = spot.defensive.length > 0 || spot.attacking.length > 0
  const notes = review.dataNotes

  return (
    <div className={first ? '' : 'page-break'} style={{ marginTop: first ? 0 : 36 }}>

      <div data-pdf-block="1" data-pdf-newpage={first ? undefined : '1'}>
        <div style={{ background: BLUE, color: 'white', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, opacity: 0.8, letterSpacing: 1 }}>{sportLabel.toUpperCase()} SEASON REVIEW</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{label}</div>
        </div>

        <SectionTitle>Season at a glance</SectionTitle>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Tile label="Played" value={s.played} />
          <Tile label="Won" value={s.wins} color={GREEN} />
          <Tile label="Drawn" value={s.draws} color={AMBER} />
          <Tile label="Lost" value={s.losses} color={RED} />
          <Tile label="Scored" value={s.pointsFor} color={BLUE} />
          <Tile label="Conceded" value={s.pointsAgainst} />
          <Tile label="Diff" value={(s.diff > 0 ? '+' : '') + s.diff} color={s.diff >= 0 ? GREEN : RED} />
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', margin: '12px 0 8px' }}>
          {s.form.map((r, i) => (
            <span key={i} title={review.games[i].opponent}
              style={{ width: 24, height: 24, borderRadius: '50%', background: resultColor(r), color: 'white', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{r}</span>
          ))}
        </div>
        <div style={{ fontSize: 12, color: '#444', lineHeight: 1.7 }}>
          {s.biggestWin && <div>Biggest win: <b>+{s.biggestWin.margin}</b> vs {s.biggestWin.opponent}</div>}
          {s.narrowestLoss && <div>Narrowest defeat: <b>{Math.abs(s.narrowestLoss.margin)}</b> {Math.abs(s.narrowestLoss.margin) === 1 ? 'point' : 'points'} vs {s.narrowestLoss.opponent}</div>}
          <div>Defeats by 3 points or fewer: <b>{s.narrowLosses}</b></div>
          <div>Games decided by 5 points or fewer: <b>{s.closeGames.total}</b> (won {s.closeGames.W}, drawn {s.closeGames.D}, lost {s.closeGames.L})</div>
        </div>
      </div>

      {narrative && (narrative.headline || narrative.patterns.length > 0) && (
        <div data-pdf-block="1">
          <SectionTitle note="Written by AI from the numbers in this report. Each team has only a handful of games, so treat these as things to discuss, not conclusions.">Key takeaways</SectionTitle>
          {narrative.headline && (
            <div style={{ background: '#EAF3DE', borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: 1.6, color: '#2d4a12', fontWeight: 600 }}>{narrative.headline}</div>
          )}
          <TakeawayList title="Highlights" icon="⭐" items={narrative.highlights} />
          {narrative.patterns.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 4 }}>🔍 Worth discussing</div>
              {narrative.patterns.map((p, i) => (
                <div key={i} style={{ background: '#f7f9fc', borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12, lineHeight: 1.6 }}>
                  <b>{p.title}</b>{p.title && p.detail ? ': ' : ''}{p.detail}
                </div>
              ))}
            </div>
          )}
          {narrative.gamesThatGotAway.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 4 }}>🎯 Games that were there to be won</div>
              {narrative.gamesThatGotAway.map((g, i) => (
                <div key={i} style={{ background: '#FAEEDA', borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12, lineHeight: 1.6 }}>
                  <b>{g.opponent}</b>{g.opponent && g.note ? ': ' : ''}{g.note}
                </div>
              ))}
            </div>
          )}
          <TakeawayList title="Reasons for optimism" icon="🌱" items={narrative.reasonsForOptimism} />
          <TakeawayList title="Training focus" icon="🏐" items={narrative.trainingFocus} />
        </div>
      )}

      <div data-pdf-block="1">
        <SectionTitle note="* half-time score estimated from logged scores">Results</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Date</th><th style={th}>Opponent</th><th style={th}>Score</th><th style={th}>Margin</th><th style={th}>HT</th></tr></thead>
            <tbody>
              {review.games.map(g => {
                const f = fxById[g.id]
                return (
                  <tr key={g.id}>
                    <td style={td}>{shortDate(g.date)}</td>
                    <td style={td}>{g.opponent}{g.gameType === 'friendly' ? ' (friendly)' : ''}</td>
                    <td style={td}>{f ? (f.home_goals || 0) + '-' + pad(f.home_points) : ''} ({g.us}) v {f ? (f.away_goals || 0) + '-' + pad(f.away_points) : ''} ({g.them})</td>
                    <td style={{ ...td, fontWeight: 700, color: resultColor(g.result) }}>{g.margin > 0 ? '+' : ''}{g.margin}</td>
                    <td style={td}>{g.atHalf ? g.atHalf.us + '–' + g.atHalf.them + (g.atHalf.exact ? '' : '*') : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div data-pdf-block="1">
        <SectionTitle note="The line is our lead or deficit through the game: green zone = we are ahead, red zone = we are behind. Green dots are our scores (bigger = goal), red dots are theirs. Each game has its own scale, shown on the left. Dashed line = half-time.">
          How each game unfolded
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
          {review.games.map(g => g.flow ? (
            <MatchFlow key={g.id} game={g} matchLen={matchLen} halfLen={halfLengthMins} />
          ) : (
            <div key={g.id} style={{ border: '1px dashed #d5dae3', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: GREY, breakInside: 'avoid' }}>
              <b>{g.opponent}</b> ({g.us}–{g.them})<br />Match timeline wasn't recorded, so only the result and scorers are used.
            </div>
          ))}
        </div>
      </div>

      {fullGames.length > 0 && (
        <div data-pdf-block="1">
          <SectionTitle note={'Points by ten-minute slice, across ' + fullGames.length + ' games with a full timeline'}>When we score and concede</SectionTitle>
          <BandsChart bands={review.bands} />
          <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#555', marginTop: 4 }}>
            <span><span style={{ color: BLUE }}>■</span> Scored by us</span>
            <span><span style={{ color: '#E08A3C' }}>■</span> Scored against us</span>
          </div>
        </div>
      )}

      {review.topScorers.length > 0 && (
        <div data-pdf-block="1">
          <SectionTitle note="Points (a goal counts as 3)">Top scorers</SectionTitle>
          {review.topScorers.map(p => (
            <div key={p.id} style={{ marginBottom: 7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                <span><b>{p.total}</b> <span style={{ color: GREY }}>· {p.goals}G {p.twoPointers > 0 ? p.twoPointers + ' x 2pt ' : ''}{p.points}pts</span></span>
              </div>
              <div style={{ height: 6, background: '#eef1f6', borderRadius: 3, margin: '3px 0' }}>
                <div style={{ width: (p.total / maxTotal) * 100 + '%', height: '100%', background: BLUE, borderRadius: 3 }} />
              </div>
              <div style={{ fontSize: 10, color: GREY }}>From play {p.fromPlay} · frees {p.fromFrees}{p.fromOther > 0 ? ' · other ' + p.fromOther : ''}</div>
            </div>
          ))}
        </div>
      )}

      <div data-pdf-block="1">
        <SectionTitle note={'Minutes are counted only in the ' + notes.fullTimelineGames + ' games with a full timeline'}>Appearances and scoring</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Player', 'GP', 'Start', 'Sub', 'Min', 'G', '2pt', 'Pts', 'Total', 'Play', 'Frees'].filter(h => showTwoPoints || h !== '2pt').map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {review.players.map(p => (
                <tr key={p.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                  <td style={td}>{p.gamesPlayed || '—'}</td>
                  <td style={td}>{p.gamesStarted || '—'}</td>
                  <td style={td}>{p.gamesAsSub || '—'}</td>
                  <td style={td}>{p.minutes || '—'}</td>
                  <td style={td}>{p.goals || '—'}</td>
                  {showTwoPoints && <td style={td}>{p.twoPointers || '—'}</td>}
                  <td style={td}>{p.points || '—'}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{p.total || '—'}</td>
                  <td style={td}>{p.fromPlay || '—'}</td>
                  <td style={td}>{p.fromFrees || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {subGames.length > 0 && (
        <div data-pdf-block="1">
          <SectionTitle note="Scoring before and after the first substitution, per 10 minutes of play (games with a full timeline)">Substitutions</SectionTitle>
          <div style={{ fontSize: 12, color: '#444', lineHeight: 1.7, marginBottom: 6 }}>
            Before the first sub we averaged <b>{per10(st.bUs, st.bMin)}</b> scored / <b>{per10(st.bThem, st.bMin)}</b> conceded per 10 minutes.
            After it: <b>{per10(st.aUs, st.aMin)}</b> scored / <b>{per10(st.aThem, st.aMin)}</b> conceded.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Game</th><th style={th}>Subs</th><th style={th}>First sub</th><th style={th}>Before (us–them)</th><th style={th}>After (us–them)</th></tr></thead>
              <tbody>
                {subGames.map(g => (
                  <tr key={g.id}>
                    <td style={td}>{g.opponent}</td>
                    <td style={td}>{g.subs.subsMade}</td>
                    <td style={td}>{g.subs.firstSubMinute}'</td>
                    <td style={td}>{g.subs.beforeFirst.us}–{g.subs.beforeFirst.them}</td>
                    <td style={td}>{g.subs.afterFirst.us}–{g.subs.afterFirst.them}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasSpot && (
        <div data-pdf-block="1">
          <SectionTitle note="A talking point only. Heavily influenced by the opposition and by teammates, and only players with 150+ minutes are included.">Worth noticing</SectionTitle>
          {spot.defensive.map(p => (
            <div key={'d' + p.id} style={{ background: '#EAF3DE', borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12 }}>
              🛡️ <b>{p.name}</b>: the team conceded {p.conceded} points per full match while on the pitch (team average {review.baseline.concededPerMatch}).
            </div>
          ))}
          {spot.attacking.map(p => (
            <div key={'a' + p.id} style={{ background: '#E6F1FB', borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12 }}>
              ⚡ <b>{p.name}</b>: the team scored {p.scored} points per full match while on the pitch (team average {review.baseline.scoredPerMatch}).
            </div>
          ))}
        </div>
      )}

      <div data-pdf-block="1">
        <div style={{ background: '#FAEEDA', color: AMBER, borderRadius: 8, padding: '8px 10px', marginTop: 20, fontSize: 11, lineHeight: 1.6 }}>
          <b>About this data:</b> {notes.fullTimelineGames} of {review.games.length} games have a full match timeline; the rest are used for results and scorers only.
          {notes.unattributedPoints > 0 && ' ' + notes.unattributedPoints + ' points were scored by players who weren\'t identified.'}
          {notes.scorerMismatchGames.length > 0 && ' Logged scorers don\'t exactly match the final score in: ' + notes.scorerMismatchGames.map(g => g.opponent + ' (' + shortDate(g.date) + ')').join(', ') + '.'}
          {' '}{sportLabel === 'football' ? 'Kickouts' : 'Puckouts'}, wides and frees are not fully recorded, so they are not used.
        </div>
      </div>
    </div>
  )
}

export default function SeasonReview({ profile }) {
  const [squads, setSquads] = useState([])
  const [squadId, setSquadId] = useState('')
  const [fixtures, setFixtures] = useState([])
  const [season, setSeason] = useState(null)
  const [sport, setSport] = useState('football')
  const [halfLengthMins, setHalfLengthMins] = useState(30)
  const [events, setEvents] = useState([])
  const [lineups, setLineups] = useState([])
  const [includeFriendlies, setIncludeFriendlies] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  const [lastLoaded, setLastLoaded] = useState(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [pdfError, setPdfError] = useState('')
  const [showEmail, setShowEmail] = useState(false)
  const [emailCoaches, setEmailCoaches] = useState([])
  const [pickedCoaches, setPickedCoaches] = useState([])
  const [emailSelf, setEmailSelf] = useState(true)
  const [freeEmail, setFreeEmail] = useState('')
  const [emailState, setEmailState] = useState(null)
  const [emailError, setEmailError] = useState('')
  const [narrative, setNarrative] = useState(null)
  const [narrativeBusy, setNarrativeBusy] = useState(false)
  const [narrativeError, setNarrativeError] = useState('')

  // 1. The squads this person can review
  useEffect(() => {
    async function loadSquads() {
      if (!profile?.club_id) return
      if (profile.role !== 'club_admin' && !(profile.squadIds || []).length) { setSquads([]); setLoading(false); return }
      let q = supabase.from('squads')
        .select('id, name, sport, current_grade, age_group, squad_teams(team_number, label)')
        .eq('club_id', profile.club_id).order('name')
      if (profile.role !== 'club_admin') q = q.in('id', profile.squadIds)
      const { data, error: err } = await q
      if (err) { setError(err.message); setLoading(false); return }
      setSquads(data || [])
      if (data && data.length > 0) setSquadId(prev => prev || data[0].id)
      else setLoading(false)
    }
    loadSquads()
  }, [profile?.club_id])

  // 2. All completed fixtures for the squad (every sport), and its half length
  useEffect(() => {
    if (!squadId) return
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      const { data, error: err } = await supabase.from('fixtures').select('*')
        .eq('squad_id', squadId).eq('status', 'completed')
        .order('fixture_date', { ascending: true })
      if (cancelled) return
      if (err) { setError(err.message); setLoading(false); return }
      const sq = squads.find(s => s.id === squadId)
      const grade = sq?.current_grade || sq?.age_group
      let half = 30
      if (grade) {
        const { data: rules } = await supabase.from('age_grade_rules').select('half_length_mins').eq('grade', grade).maybeSingle()
        if (rules?.half_length_mins) half = rules.half_length_mins
      }
      if (cancelled) return
      setHalfLengthMins(half)
      setFixtures(data || [])
      const sportsFound = [...new Set((data || []).map(f => f.sport))]
      setSport(prev => sportsFound.includes(prev) ? prev : (sportsFound.includes('football') ? 'football' : (sportsFound[0] || prev)))
      const seasons = [...new Set((data || []).map(f => f.season).filter(Boolean))].sort((a, b) => b - a)
      const thisYear = new Date().getFullYear()
      setSeason(prev => (prev && seasons.includes(prev)) ? prev : (seasons.includes(thisYear) ? thisYear : (seasons[0] || null)))
      if (seasons.length === 0) setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [squadId, reloadKey])

  // 3. Events and lineups for the chosen sport and season (events are fetched in pages of 1000)
  useEffect(() => {
    if (!squadId || season === null) return
    const ids = fixtures.filter(f => f.season === season && f.sport === sport).map(f => f.id)
    if (ids.length === 0) { setEvents([]); setLineups([]); setLoading(false); return }
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const evs = await fetchAllEvents(ids)
        const { data: lus, error: lErr } = await supabase.from('lineups')
          .select('*, players(id, full_name, jersey_number)').in('fixture_id', ids)
        if (lErr) throw lErr
        if (cancelled) return
        setEvents(evs)
        setLineups(lus || [])
        setLastLoaded(new Date())
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load season data')
      }
      if (!cancelled) setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [fixtures, season, squadId, sport])

  const sportFixtures = useMemo(() => fixtures.filter(f => f.sport === sport), [fixtures, sport])
  const availableSports = useMemo(() => [...new Set(fixtures.map(f => f.sport).filter(Boolean))].sort(), [fixtures])
  const seasonFixtures = useMemo(() => sportFixtures.filter(f => f.season === season), [sportFixtures, season])
  const availableSeasons = useMemo(() => [...new Set(sportFixtures.map(f => f.season).filter(Boolean))].sort((a, b) => b - a), [sportFixtures])

  // If the chosen season has no games in this sport, move to one that does
  useEffect(() => {
    if (availableSeasons.length > 0 && !availableSeasons.includes(season)) setSeason(availableSeasons[0])
  }, [availableSeasons])

  const reviews = useMemo(() => {
    const nums = teamNumbersIn(eligibleFixtures(seasonFixtures, { includeFriendlies }))
    return nums.map(n => buildTeamReview({ teamNumber: n, fixtures: seasonFixtures, lineups, events, halfLengthMins, includeFriendlies }))
  }, [seasonFixtures, lineups, events, halfLengthMins, includeFriendlies])

  const squad = squads.find(s => s.id === squadId)
  const sportLabel = sport === 'hurling' ? (squad?.sport === 'camogie' ? 'camogie' : 'hurling') : 'football'
  const sportTitle = sportLabel.charAt(0).toUpperCase() + sportLabel.slice(1)
  const clubName = profile?.clubs?.club_name || profile?.clubs?.name || ''
  const selectStyle = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d5dae3', fontSize: 13, background: 'white' }

  // Builds an A4 PDF by capturing each report section as an image, so no section is split mid-way.
  async function buildPdf(opts = {}) {
    const html2canvas = (await import('html2canvas')).default
    const { jsPDF } = await import('jspdf')
    const root = document.getElementById('season-review-print')
    if (!root) throw new Error('The report is not ready yet')
    window.scrollTo(0, 0)
    const blocks = Array.from(root.querySelectorAll('[data-pdf-block]'))
    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const margin = 28
    const topY = 32
    const bottomY = pageH - 36
    const usableW = pageW - margin * 2
    const capacity = bottomY - topY
    let y = topY
    let started = false

    for (const el of blocks) {
      const canvas = await html2canvas(el, {
        scale: opts.scale || 1.6,
        backgroundColor: '#ffffff',
        windowWidth: 900,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        onclone: (clonedDoc) => {
          const r = clonedDoc.getElementById('season-review-print')
          if (r) { r.style.width = '820px'; r.style.maxWidth = '820px' }
        },
      })
      if (!canvas.width || !canvas.height) continue
      const ratio = usableW / canvas.width
      const fullH = canvas.height * ratio
      const wantsNewPage = el.getAttribute('data-pdf-newpage') === '1'

      if (started) {
        const remaining = bottomY - y
        if (wantsNewPage || (fullH <= capacity && fullH > remaining) || (fullH > capacity && remaining < 160)) {
          doc.addPage()
          y = topY
        }
      }
      started = true

      let srcY = 0
      while (srcY < canvas.height) {
        const sliceH = Math.min(canvas.height - srcY, Math.floor((bottomY - y) / ratio))
        if (sliceH <= 0) { doc.addPage(); y = topY; continue }
        const slice = document.createElement('canvas')
        slice.width = canvas.width
        slice.height = sliceH
        const ctx = slice.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, slice.width, slice.height)
        ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH)
        doc.addImage(slice.toDataURL('image/jpeg', opts.quality || 0.88), 'JPEG', margin, y, usableW, sliceH * ratio)
        srcY += sliceH
        y += sliceH * ratio + 10
        if (srcY < canvas.height) { doc.addPage(); y = topY }
      }
    }

    const total = doc.getNumberOfPages()
    for (let i = 1; i <= total; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text('Generated via GAA Match Tracker - gaamatchtracker.ie - coach-only', margin, pageH - 18)
      doc.text('Page ' + i + ' of ' + total, pageW - margin, pageH - 18, { align: 'right' })
    }
    return doc
  }

  async function savePdf() {
    setPdfBusy(true)
    setPdfError('')
    try {
      const doc = await buildPdf()
      const fileName = (clubName + ' ' + (squad?.name || '') + ' ' + sportLabel + ' season review ' + season)
        .replace(/[^a-z0-9 ]/gi, '').trim().replace(/\s+/g, '-')
      doc.save(fileName + '.pdf')
    } catch (e) {
      setPdfError(e.message || 'Could not build the PDF')
    }
    setPdfBusy(false)
  }

  // Saved key takeaways for this squad, season and sport
  useEffect(() => {
    if (!squadId || season === null) return
    let cancelled = false
    setNarrative(null)
    setNarrativeError('')
    supabase.from('season_review_narratives').select('content, generated_at')
      .eq('squad_id', squadId).eq('season', season).eq('sport', sport).maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) setNarrative({ ...data.content, generatedAt: data.generated_at })
      })
    return () => { cancelled = true }
  }, [squadId, season, sport])

  async function generateNarrative() {
    setNarrativeBusy(true)
    setNarrativeError('')
    try {
      const teams = reviews.map(r => ({
        teamNumber: r.teamNumber,
        label: getTeamLabel(r.teamNumber, squad?.squad_teams),
        facts: buildInsightFacts(r, halfLengthMins),
      }))
      const { data, error: fnError } = await supabase.functions.invoke('generate-season-review', {
        body: { squadId, season, sport, clubName, squadName: squad?.name || '', includeFriendlies, teams },
      })
      if (fnError) {
        let msg = fnError.message
        try {
          const errBody = await fnError.context.json()
          if (errBody?.error) msg = errBody.error
        } catch (e) { /* keep the default message */ }
        throw new Error(msg)
      }
      if (data?.error) throw new Error(data.error)
      setNarrative({ ...data.narrative, generatedAt: data.generatedAt })
      if (data.saved === false) setNarrativeError('Written, but it could not be saved, so it will disappear if you leave this page.')
    } catch (e) {
      setNarrativeError(e.message || 'Could not generate the key takeaways')
    }
    setNarrativeBusy(false)
  }

  // Coaches on this squad, for the email picker (loaded when the panel is opened)
  useEffect(() => {
    if (!showEmail || !squadId) return
    let cancelled = false
    async function loadEmailCoaches() {
      const { data } = await supabase.from('squad_members')
        .select('profile_id, profiles!squad_members_profile_id_fkey(id, full_name, email)')
        .eq('squad_id', squadId).eq('approved', true)
      if (cancelled) return
      const list = (data || []).map(m => m.profiles).filter(p => p && p.id !== profile?.id)
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
      setEmailCoaches(list)
    }
    loadEmailCoaches()
    return () => { cancelled = true }
  }, [showEmail, squadId])

  async function sendReportEmail() {
    const recipients = []
    if (emailSelf) {
      const { data: auth } = await supabase.auth.getUser()
      if (auth?.user?.email) recipients.push(auth.user.email)
    }
    emailCoaches.filter(c => pickedCoaches.includes(c.id) && c.email).forEach(c => recipients.push(c.email))
    const extra = freeEmail.trim()
    if (extra) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(extra)) { setEmailError("That email address doesn't look right."); setEmailState('error'); return }
      recipients.push(extra)
    }
    const unique = [...new Set(recipients.map(r => r.toLowerCase()))]
    if (unique.length === 0) { setEmailError('Choose at least one recipient.'); setEmailState('error'); return }

    setEmailState('sending')
    setEmailError('')
    try {
      const doc = await buildPdf({ scale: 1.25, quality: 0.78 })
      const pdfBase64 = doc.output('datauristring').split('base64,')[1]
      const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
      const title = clubName + ' ' + (squad?.name || '') + ' ' + sportLabel + ' season review ' + season
      const fileName = title.replace(/[^a-z0-9 ]/gi, '').trim().replace(/\s+/g, '-') + '.pdf'
      const htmlBody = `
        <div style="font-family: sans-serif; max-width: 480px; margin: 0 auto;">
          <div style="background: #185FA5; padding: 24px; border-radius: 12px 12px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 20px;">${esc(sportTitle)} Season Review</h1>
          </div>
          <div style="background: white; padding: 28px; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px;">
            <p style="color: #555; line-height: 1.6;"><strong>${esc(clubName)}</strong> · ${esc(squad?.name)} · Season ${esc(season)}</p>
            <p style="color: #888; font-size: 13px;">Attached as a PDF. This report is for coaches and includes player names, so please don't forward it on.</p>
          </div>
        </div>`
      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({ type: 'team_sheet', toEmails: unique, subject: title, htmlBody, pdfBase64, fileName })
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to send')
      }
      setEmailState('sent')
    } catch (e) {
      setEmailState('error')
      setEmailError(e.message || 'Failed to send')
    }
  }

  return (
    <div style={{ padding: '12px 14px' }}>
      <style>{printCss}</style>

      <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        {squads.length > 1 && (
          <select value={squadId} onChange={e => setSquadId(e.target.value)} style={selectStyle}>
            {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {availableSports.length > 1 && (
          <select value={sport} onChange={e => setSport(e.target.value)} style={selectStyle}>
            {availableSports.map(sp => <option key={sp} value={sp}>{sp === 'hurling' ? (squad?.sport === 'camogie' ? 'Camogie' : 'Hurling') : 'Football'}</option>)}
          </select>
        )}
        {availableSeasons.length > 0 && (
          <select value={season || ''} onChange={e => setSeason(Number(e.target.value))} style={selectStyle}>
            {availableSeasons.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={includeFriendlies} onChange={e => setIncludeFriendlies(e.target.checked)} /> Include friendlies
        </label>
        <button onClick={() => setReloadKey(k => k + 1)} disabled={loading}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid ' + BLUE, background: '#E6F1FB', color: BLUE, fontSize: 13, cursor: 'pointer' }}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
        <button onClick={generateNarrative} disabled={loading || reviews.length === 0 || narrativeBusy}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #533AB7', background: '#EEEDFE', color: '#533AB7', fontSize: 13, cursor: 'pointer', opacity: narrativeBusy ? 0.7 : 1 }}>
          {narrativeBusy ? 'Writing… (up to a minute)' : narrative ? '✨ Regenerate takeaways' : '✨ Generate takeaways'}
        </button>
        <button onClick={() => window.print()} disabled={loading || reviews.length === 0}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid ' + BLUE, background: 'white', color: BLUE, fontSize: 13, cursor: 'pointer' }}>
          🖨️ Print
        </button>
        <button onClick={savePdf} disabled={loading || reviews.length === 0 || pdfBusy}
          style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: BLUE, color: 'white', fontSize: 13, cursor: 'pointer', opacity: pdfBusy ? 0.7 : 1 }}>
          {pdfBusy ? 'Building PDF…' : '⬇️ Save PDF'}
        </button>
        <button onClick={() => { setShowEmail(s => !s); setEmailState(null); setEmailError('') }} disabled={loading || reviews.length === 0}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid ' + BLUE, background: showEmail ? '#E6F1FB' : 'white', color: BLUE, fontSize: 13, cursor: 'pointer' }}>
          ✉️ Email
        </button>
      </div>
      {showEmail && (
        <div className="no-print" style={{ border: '1px solid #d5dae3', borderRadius: 10, padding: 12, marginBottom: 12, background: '#fafbfd' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Email this report as a PDF</div>
          <div style={{ fontSize: 11, color: GREY, marginBottom: 8 }}>It names players, so send it to coaches only. It's built from the numbers on screen, so press Refresh first if results have changed.</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 6 }}>
            <input type="checkbox" checked={emailSelf} onChange={e => setEmailSelf(e.target.checked)} /> Email to me
          </label>
          {emailCoaches.map(c => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 6, opacity: c.email ? 1 : 0.5 }}>
              <input type="checkbox" disabled={!c.email} checked={pickedCoaches.includes(c.id)}
                onChange={() => setPickedCoaches(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])} />
              {c.full_name || 'Coach'}{c.email ? '' : ' (no email on file)'}
            </label>
          ))}
          <input type="email" placeholder="Another email address (optional)" value={freeEmail} onChange={e => setFreeEmail(e.target.value)}
            style={{ ...selectStyle, width: '100%', boxSizing: 'border-box', margin: '4px 0 8px' }} />
          <button onClick={sendReportEmail} disabled={emailState === 'sending'}
            style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: BLUE, color: 'white', fontSize: 13, cursor: 'pointer', opacity: emailState === 'sending' ? 0.7 : 1 }}>
            {emailState === 'sending' ? 'Building and sending…' : 'Send'}
          </button>
          {emailState === 'sent' && <div style={{ fontSize: 12, color: GREEN, marginTop: 8 }}>✅ Sent.</div>}
          {emailState === 'error' && <div style={{ fontSize: 12, color: RED, marginTop: 8 }}>{emailError}</div>}
        </div>
      )}
      {lastLoaded && !loading && (
        <div className="no-print" style={{ fontSize: 11, color: GREY, marginBottom: 10 }}>
          Coach-only report. Calculated from completed games at {lastLoaded.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}. Press Refresh after new results are in.
        </div>
      )}

      {error && <div className="no-print" style={{ background: '#FCEBEB', color: RED, padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 10 }}>Couldn't load the review: {error}</div>}
      {pdfError && <div className="no-print" style={{ background: '#FCEBEB', color: RED, padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 10 }}>Couldn't build the PDF: {pdfError}. Print still works.</div>}
      {!loading && reviews.length > 0 && (
        <div className="no-print" style={{ fontSize: 12, marginBottom: 10 }}>
          {narrativeError && <div style={{ background: '#FCEBEB', color: RED, padding: 10, borderRadius: 8, marginBottom: 6 }}>{narrativeError}</div>}
          {narrative ? (
            <div style={{ color: '#555' }}>
              ✨ Key takeaways written {narrative.generatedAt ? new Date(narrative.generatedAt).toLocaleString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}, covering {narrative.gamesCovered} games.
              {narrative.gamesCovered !== reviews.reduce((n, r) => n + r.summary.played, 0) && (
                <span style={{ color: AMBER, fontWeight: 600 }}> The number of games has changed since then, so press Regenerate.</span>
              )}
            </div>
          ) : (
            <div style={{ color: GREY }}>No key takeaways yet. Press ✨ Generate takeaways to add an AI-written summary to the report.</div>
          )}
        </div>
      )}
      {loading && <div style={{ padding: 30, textAlign: 'center', color: GREY, fontSize: 14 }}>Crunching the season…</div>}
      {!loading && !error && reviews.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: GREY, fontSize: 14 }}>No completed {sportLabel} games found for this squad and season.</div>
      )}

      {!loading && reviews.length > 0 && (
        <div id="season-review-print">
          <div data-pdf-block="1" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: GREY }}>{clubName} · {squad?.name} · Season {season}</div>
            <div style={{ fontSize: 11, color: GREY }}>{includeFriendlies ? 'Includes friendly matches' : 'League and championship games only (friendlies excluded)'}</div>
          </div>
          {reviews.map((r, i) => (
            <TeamSection key={r.teamNumber} review={r} first={i === 0}
              narrative={narrative?.teams?.find(t => Number(t.teamNumber) === r.teamNumber)}
              label={getTeamLabel(r.teamNumber, squad?.squad_teams)}
              fixtures={seasonFixtures} halfLengthMins={halfLengthMins} sportLabel={sportLabel} />
          ))}
        </div>
      )}
    </div>
  )
}